import React, { useEffect, useMemo, useState } from "react";
import { Alert, Collapse, Radio, Select, Spin, Switch } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../../app/store";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
// REDUX
import { useGetMtlsSecretsQuery } from "../../../redux/channels/channelAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";

const { Panel } = Collapse;
const { Option } = Select;

const SECRET_NAME_CONVENTION = "liftnet/fleet/devices/<host>/connect-bundle";

type MtlsSecretSelectionMode = "manual" | "list" | "derive";

/** HTTP status of an RTK Query error, or undefined when it has none. */
function errorStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "status" in error) {
    const { status } = error as { status: unknown };
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

/**
 * Strips a trailing ":port" (including bracketed IPv6 hosts) from a vendor
 * socket address, leaving just the host. Returns "" when no host can be
 * determined.
 */
export function extractHostFromSocketAddress(address: string): string {
  const trimmed = address.trim();
  if (!trimmed) return "";
  const ipv6Match = trimmed.match(/^\[([0-9a-fA-F:]+)\](?::\d+)?$/);
  if (ipv6Match) return ipv6Match[1];
  const colonIdx = trimmed.lastIndexOf(":");
  if (colonIdx === -1) return trimmed;
  return trimmed.substring(0, colonIdx);
}

/**
 * Derives the conventional Secrets Manager reference name for a channel's
 * mTLS client certificate bundle. Prefers the vendor device's host (stable
 * per-device identity, matches rpi-flasher's naming convention); falls back
 * to the channel id when no vendor address is known yet (e.g. a brand-new,
 * unsaved channel). Returns "" when neither is available — the caller must
 * hint that a vendor address or saved channel is required.
 */
export function deriveMtlsSecretName(
  vendorSocketAddress?: string | null,
  channelId?: string | null,
): string {
  const host = vendorSocketAddress ? extractHostFromSocketAddress(String(vendorSocketAddress)) : "";
  if (host) return `liftnet/fleet/devices/${host}/connect-bundle`;
  if (channelId) return `liftnet/fleet/channels/${channelId}/connect-bundle`;
  return "";
}

interface ChannelMtlsEditorProps {
  enabled: boolean;
  secretName: string;
  onChangeEnabled: (enabled: boolean) => void;
  onChangeSecretName: (secretName: string) => void;
  vendorSocketAddress?: string | null;
  channelId?: string | null;
}

export default function ChannelMtlsEditor({
  enabled,
  secretName,
  onChangeEnabled,
  onChangeSecretName,
  vendorSocketAddress = null,
  channelId = null,
}: ChannelMtlsEditorProps) {
  const [t] = useTranslation("global");
  const [selectionMode, setSelectionMode] = useState<MtlsSecretSelectionMode>("manual");

  // GET /channels/mtls-secrets is MASTER ADMIN only (LIFT-1085): the secret
  // namespace is fleet-wide, so listing it would show every tenant's bundle
  // names. The server decides by role name, so this asks the same question
  // (no org-id fallback); anyone else types or derives the name instead.
  const userLogged = useSelector((state: RootState) => state.home?.userLogged);
  const canListSecrets = isMasterAdmin(userLogged);

  const derivedName = useMemo(
    () => deriveMtlsSecretName(vendorSocketAddress, channelId),
    [vendorSocketAddress, channelId],
  );

  // Keep the derived name pushed to the parent while "derive" mode is
  // active, so an address typed after switching modes still lands in
  // secretName (and re-derives if the vendor address changes later).
  // onChangeSecretName is intentionally omitted from deps — parent
  // callbacks here are not memoized, and this effect only needs to react
  // to mode/name changes (same convention as the rest of this file's
  // effects, e.g. StepChannels.tsx's fetchExistingChannels effect).
  useEffect(() => {
    if (selectionMode === "derive") {
      onChangeSecretName(derivedName);
    }
  }, [selectionMode, derivedName]);

  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;
    // Some test mocks reset GETJwtToken to a bare jest.fn() (undefined return).
    // Promise.resolve keeps this path safe while preserving normal async flow.
    Promise.resolve(GETJwtToken())
      .then((jwt) => {
        if (!isMounted) return;
        setToken(jwt ?? null);
      })
      .catch(() => {
        if (!isMounted) return;
        setToken(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const {
    data: secretsData,
    isFetching: isFetchingSecrets,
    isError: isSecretsError,
    error: secretsError,
  } = useGetMtlsSecretsQuery(
    { prefix: selectionMode === "list" ? "liftnet/fleet/" : undefined, token: token ?? undefined },
    { skip: !enabled || selectionMode !== "list" || !token || !canListSecrets },
  );
  // A 403 means the server does not let this role list secrets (the role check
  // above can disagree with it, e.g. while the user is still loading). That is
  // not a failure to retry or report as one.
  const secretsForbidden = isSecretsError && errorStatus(secretsError) === 403;

  const secretOptions = secretsData?.secrets ?? [];

  return (
    <Collapse style={{ marginTop: 12 }}>
      <Panel header="mTLS Client Certificate (per-channel)" key="mtls">
        <div className="generalStyles__flex" style={{ marginBottom: 16 }}>
          <span className="generalStyles__inputLabel">Enable mTLS</span>
          <Switch
            data-testid="mtls-enabled-switch"
            checked={enabled}
            onChange={onChangeEnabled}
            style={{ marginLeft: 8 }}
          />
        </div>
        {enabled && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Radio.Group
                data-testid="mtls-selection-mode"
                value={selectionMode}
                onChange={(e) => setSelectionMode(e.target.value as MtlsSecretSelectionMode)}
              >
                <Radio.Button value="manual">Enter name</Radio.Button>
                <Radio.Button value="list" disabled={!canListSecrets}>
                  Pick from list
                </Radio.Button>
                <Radio.Button value="derive">Auto-derive</Radio.Button>
              </Radio.Group>
              {!canListSecrets && (
                <span
                  data-testid="mtls-list-not-available"
                  style={{ color: "#888", fontSize: 12, display: "block", marginTop: 8 }}
                >
                  {t("edgeManagement.channels.mtlsSecretsListNotAvailable")}
                </span>
              )}
            </div>

            {selectionMode === "manual" && (
              <FormInputText
                inputValue={secretName}
                inputLabelName="Secret Name / ARN"
                inputOnChange={(value) => onChangeSecretName(value)}
                inputVariableName="mtlsSecretName"
                isRequired={false}
                disabledInput={false}
              />
            )}

            {selectionMode === "list" && (
              <div>
                <span className="generalStyles__inputLabel">Secret</span>
                <Spin spinning={isFetchingSecrets}>
                  <Select
                    data-testid="mtls-secret-select"
                    className="generalStyles__width100"
                    style={{ marginTop: 8 }}
                    size="large"
                    value={secretName || undefined}
                    placeholder={
                      isSecretsError
                        ? "Unable to load secrets — switch to Enter name"
                        : "Select a secret"
                    }
                    disabled={isSecretsError || (!isFetchingSecrets && secretOptions.length === 0)}
                    onChange={(value: string) => onChangeSecretName(value)}
                  >
                    {secretOptions.map((s) => (
                      <Option key={s.arn} value={s.name}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                </Spin>
                {secretsForbidden && (
                  <Alert
                    data-testid="mtls-secrets-forbidden"
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                    message={t("edgeManagement.channels.mtlsSecretsListNotAvailable")}
                  />
                )}
                {isSecretsError && !secretsForbidden && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                    message="Could not load the Secrets Manager list. Switch to “Enter name” to type it manually."
                  />
                )}
                {!isSecretsError && !isFetchingSecrets && secretOptions.length === 0 && (
                  <span style={{ color: "#888", fontSize: 12, display: "block", marginTop: 8 }}>
                    No mTLS secrets found. Switch to “Enter name” or “Auto-derive”.
                  </span>
                )}
              </div>
            )}

            {selectionMode === "derive" && (
              <div>
                <span className="generalStyles__inputLabel">Derived Secret Name</span>
                <div style={{ marginTop: 8 }}>
                  <FormInputText
                    inputValue={derivedName}
                    inputLabelName=""
                    inputOnChange={() => {}}
                    inputVariableName="mtlsDerivedSecretName"
                    isRequired={false}
                    disabledInput
                  />
                </div>
                {!derivedName && (
                  <span className="generalStyles__error" style={{ fontSize: 12 }}>
                    A vendor address or a saved channel is required to auto-derive a name.
                  </span>
                )}
              </div>
            )}

            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message="The referenced secret must already exist in Secrets Manager, created by provisioning."
              description={`Naming convention: ${SECRET_NAME_CONVENTION}`}
            />
          </>
        )}
      </Panel>
    </Collapse>
  );
}
